import { Localized } from "@fluent/react";
import { unwrapResult } from "@reduxjs/toolkit";
import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
    loadProjectProcess,
    ProjectProcess,
} from "../../features/project/projectProcess/ProjectProcessApiClient";

import { selectProjectProcessById } from "../../features/project/projectProcess/projectProcessSlice";
import { loadValidationProject } from "../../features/project/validationProject/validationProjectSlice";
import { selectDocument } from "../../features/documents/documentSlice";
import { selectDeviceById } from "../../features/device/deviceSlice";

import { AppId } from "../appTypes";
import { useAppDispatch, useAppSelector } from "../hooks";
import { AppBreadcrumbItem, AppBreadcrumbs } from "./AppBreadcrumbs";

import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import DevicesIcon from "@mui/icons-material/Devices";

export type BreadcrumbEntityType = "project" | "process" | "document" | "device";
export type BreadcrumbContext = "list" | "project" | "process" | "device" | null;

export interface BreadcrumbEntityRef {
    type: BreadcrumbEntityType;
    id: AppId;
}

export interface BreadcrumbNavigationTarget extends BreadcrumbEntityRef {
    documentType?: string;
}

const appendContext = (
    path: string,
    values: Record<string, string | undefined>
) => {
    const params = new URLSearchParams();

    Object.entries(values).forEach(([key, value]) => {
        if (value) {
            params.set(key, value);
        }
    });

    const query = params.toString();

    return query ? `${path}?${query}` : path;
};

export const entityRoutes = {
    projectList: () => "/validation-project",

    deviceList: () => "/devices",

    device: (
        id: AppId,
        context?: BreadcrumbContext,
        projectId?: AppId
    ) =>
        appendContext(`/devices/edit/${id}`, {
            context: context === "project" ? "project" : undefined,
            projectId: projectId ? String(projectId) : undefined,
        }),

    project: (id: AppId, tab?: string, context?: BreadcrumbContext) =>
        appendContext(`/validation-project/edit/${id}`, {
            tab,
            context: context === "list" ? "list" : undefined,
        }),

    processList: () => "/validation-processes",

    process: (
        id: AppId,
        context?: BreadcrumbContext,
        origin?: BreadcrumbContext,
        deviceId?: AppId,
        projectId?: AppId
    ) =>
        appendContext(`/validation-processes/edit/${id}`, {
            context:
                context === "project" || context === "list" || context === "device"
                    ? context
                    : undefined,
            origin: origin === "list" ? "list" : undefined,
            deviceId: deviceId ? String(deviceId) : undefined,
            projectId: projectId ? String(projectId) : undefined,
        }),

    document: (
        id: AppId,
        documentType?: string,
        context?: BreadcrumbContext,
        processId?: AppId,
        projectId?: AppId,
        origin?: BreadcrumbContext,
        deviceId?: AppId
    ) => {
        const normalizedType = documentType?.toUpperCase();

        const path =
            normalizedType === "URS"
                ? `/urs/edit/${id}`
                : normalizedType === "RA"
                  ? `/new-risk-analysis/edit/${id}`
                  : `/document/edit/${id}`;

        return appendContext(path, {
            context: context ?? undefined,
            processId: processId ? String(processId) : undefined,
            projectId: projectId ? String(projectId) : undefined,
            origin: origin === "list" ? "list" : undefined,
            deviceId: deviceId ? String(deviceId) : undefined,
        });
    },
};

export const useEntityNavigation = (current: BreadcrumbEntityRef) => {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const currentContext = params.get("context") as BreadcrumbContext;
    const origin = params.get("origin") === "list" || currentContext === "list"
        ? "list"
        : null;

    return useCallback(
        (target: BreadcrumbNavigationTarget) => {
            if (target.type === "project") {
                navigate(entityRoutes.project(target.id));
                return;
            }

            if (target.type === "device") {
                const context =
                    current.type === "project" || currentContext === "project"
                        ? "project"
                        : null;
                const projectId =
                    current.type === "project"
                        ? current.id
                        : params.get("projectId") ?? undefined;

                navigate(entityRoutes.device(target.id, context, projectId));
                return;
            }

            if (target.type === "process") {
                const context: BreadcrumbContext =
                    current.type === "project" || currentContext === "project"
                        ? "project"
                        : current.type === "device" || currentContext === "device"
                          ? "device"
                          : null;
                const deviceId =
                    current.type === "device"
                        ? current.id
                        : params.get("deviceId") ?? undefined;
                const projectId =
                    current.type === "project"
                        ? current.id
                        : params.get("projectId") ?? undefined;

                navigate(entityRoutes.process(target.id, context, origin, deviceId, projectId));
                return;
            }

            const context: BreadcrumbContext =
                current.type === "project" || currentContext === "project"
                    ? "project"
                                        : currentContext === "device"
                                            ? "device"
                                            : "process";

            const processId =
                current.type === "process"
                    ? current.id
                    : params.get("processId") ?? undefined;

            const projectId =
                current.type === "project"
                    ? current.id
                    : params.get("projectId") ?? undefined;

            const deviceId = params.get("deviceId") ?? undefined;

            navigate(
                entityRoutes.document(
                    target.id,
                    target.documentType,
                    context,
                    processId,
                    projectId,
                    origin,
                    deviceId
                )
            );
        },
        [
            current.id,
            current.type,
            currentContext,
            navigate,
            origin,
            params,
        ]
    );
};

export const EntityBreadcrumbs = ({
    current,
}: {
    current: BreadcrumbEntityRef;
}) => {
    const dispatch = useAppDispatch();
    const [params] = useSearchParams();

    const context = params.get("context") as BreadcrumbContext;
    const origin = params.get("origin") === "list" || context === "list"
        ? "list"
        : null;

    const documentState = useAppSelector(selectDocument).doc?.header;

    const currentDocument =
        current.type === "document" &&
        String(documentState?.id) === String(current.id)
            ? documentState
            : undefined;

    const deviceId =
        current.type === "device"
            ? String(current.id)
            : (current.type === "process" || current.type === "document") &&
                context === "device"
              ? params.get("deviceId") ?? undefined
              : undefined;

    const device = useAppSelector((state) =>
        deviceId ? selectDeviceById(state, deviceId) : undefined
    );

    const processId =
        current.type === "process"
            ? String(current.id)
            : current.type === "document" &&
                                (context === "process" || context === "project" || context === "device")
              ? params.get("processId") ??
                currentDocument?.projectProcessId
              : undefined;

    const storedProcess = useAppSelector((state) =>
        processId
            ? selectProjectProcessById(
                  state.projectProcess,
                  processId
              )
            : undefined
    );

    const [loadedProcess, setLoadedProcess] =
        useState<ProjectProcess>();

    const process = storedProcess ?? loadedProcess;

        const projectIdFromParams = params.get("projectId") ?? undefined;

        const projectId =
        current.type === "project" && context === "list"
            ? String(current.id)
                        : projectIdFromParams
                            ? projectIdFromParams
              : context === "project" && process?.projectId
                ? String(process.projectId)
                : undefined;

        const isProjectContext =
                context === "project" ||
                (origin === "list" && Boolean(projectId));

    const [projectNo, setProjectNo] = useState<string>();

    useEffect(() => {
        if (!processId || storedProcess) {
            setLoadedProcess(undefined);
            return;
        }

        let active = true;

        dispatch(loadProjectProcess(processId))
            .then(unwrapResult)
            .then((result) => {
                if (active) {
                    setLoadedProcess(result);
                }
            })
            .catch(() => {
                if (active) {
                    setLoadedProcess(undefined);
                }
            });

        return () => {
            active = false;
        };
    }, [
        dispatch,
        processId,
        storedProcess,
    ]);

    useEffect(() => {
        const processProjectNo = process?.projectNo;

        if (!projectId || processProjectNo) {
            setProjectNo(processProjectNo || undefined);
            return;
        }

        let active = true;

        dispatch(loadValidationProject(projectId))
            .then(unwrapResult)
            .then((project) => {
                if (active) {
                    setProjectNo(project.no);
                }
            })
            .catch(() => {
                if (active) {
                    setProjectNo(undefined);
                }
            });

        return () => {
            active = false;
        };
    }, [
        dispatch,
        process?.projectNo,
        projectId,
    ]);

    const hasParent =
        current.type === "device" ||
        (current.type === "project" && context === "list") ||
        (current.type === "process" &&
            (context === "list" ||
                (isProjectContext && Boolean(projectId)) ||
                (context === "device" && Boolean(deviceId)))) ||
        (current.type === "document" &&
            ((isProjectContext && Boolean(projectId)) ||
                (context === "process" && Boolean(processId)) ||
                (context === "device" && Boolean(deviceId) && Boolean(processId))));

    if (!hasParent) {
        return null;
    }

    const projectItem = (
        currentItem: boolean
    ): AppBreadcrumbItem => ({
        label: projectNo || "-",

        secondaryLabel: (
            <Localized id="project">
                Projekt
            </Localized>
        ),

        icon: <DashboardIcon />,

        to:
            currentItem || !projectId
                ? undefined
                : entityRoutes.project(
                      projectId,
                        current.type === "device" ? "devices" : "process",
                        origin
                  ),
    });

    const processItem = (
        currentItem: boolean
    ): AppBreadcrumbItem => ({
        label: process?.no || "-",

        secondaryLabel: (
            <Localized id="process">
                Proces
            </Localized>
        ),

        icon: <AccountTreeIcon />,

        to:
            currentItem || !processId
                ? undefined
                : entityRoutes.process(
                      processId,
                        isProjectContext
                          ? "project"
                          : context === "device"
                            ? "device"
                            : origin,
                      origin,
                      deviceId,
                      projectId
                  ),
    });

    const documentItem: AppBreadcrumbItem = {
        label: currentDocument?.no || "-",

        secondaryLabel: (
            <Localized id="document">
                Dokument
            </Localized>
        ),

        icon: <DescriptionIcon />,
    };

    const deviceItem = (currentItem: boolean): AppBreadcrumbItem => ({
        label: device?.name || "-",

        secondaryLabel: (
            <Localized id="devices-edit">
                Urządzenie
            </Localized>
        ),

        icon: <DevicesIcon />,

        to:
            currentItem || !deviceId
                ? undefined
                : entityRoutes.device(deviceId),
    });

    const projectListItem: AppBreadcrumbItem = {
        label: (
            <Localized id="menu-projects-list">
                Projekty
            </Localized>
        ),

        secondaryLabel: (
            <Localized id="menu-homepage">
                Strona główna
            </Localized>
        ),

        icon: <HomeIcon />,

        to: entityRoutes.projectList(),
    };

    const processListItem: AppBreadcrumbItem = {
        label: (
            <Localized id="menu-processes">
                Procesy
            </Localized>
        ),

        secondaryLabel: (
            <Localized id="menu-homepage">
                Strona główna
            </Localized>
        ),

        icon: <HomeIcon />,

        to: entityRoutes.processList(),
    };

    const deviceListItem: AppBreadcrumbItem = {
        label: (
            <Localized id="menu-devices">
                Urządzenia
            </Localized>
        ),

        secondaryLabel: (
            <Localized id="menu-homepage">
                Strona główna
            </Localized>
        ),

        icon: <HomeIcon />,

        to: entityRoutes.deviceList(),
    };

    let items: AppBreadcrumbItem[];

    if (
        current.type === "device" &&
        isProjectContext &&
        projectId
    ) {
        items = [
            projectListItem,
            projectItem(false),
            deviceItem(true),
        ];
    } else if (current.type === "device") {
        items = [
            deviceListItem,
            deviceItem(true),
        ];
    } else if (
        current.type === "document" &&
        context === "device" &&
        deviceId &&
        processId
    ) {
        items = [
            deviceListItem,
            deviceItem(false),
            processItem(false),
            documentItem,
        ];
    } else if (
        current.type === "process" &&
        context === "device" &&
        deviceId
    ) {
        items = [
            deviceListItem,
            deviceItem(false),
            processItem(true),
        ];
    } else if (current.type === "project") {
        items = [
            projectListItem,
            projectItem(true),
        ];
    } else if (
        current.type === "process" &&
        isProjectContext &&
        projectId
    ) {
        items = [
            projectListItem,
            projectItem(false),
            processItem(true),
        ];
    } else if (current.type === "process") {
        items = [
            processListItem,
            processItem(true),
        ];
    } else if (
        isProjectContext &&
        projectId &&
        processId
    ) {
        items = [
            projectListItem,
            projectItem(false),
            processItem(false),
            documentItem,
        ];
    } else if (
        isProjectContext &&
        projectId
    ) {
        items = [
            projectListItem,
            projectItem(false),
            documentItem,
        ];
    } else if (
        context === "process" &&
        processId
    ) {
        items = [
            processListItem,
            processItem(false),
            documentItem,
        ];
    } else {
        items = [
            documentItem,
        ];
    }

    return (
        <Box
            sx={{
                width: "100%",
                pb: 0.5,
                mb: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
            }}
        >
            <AppBreadcrumbs items={items} />
        </Box>
    );
};